import { type ReactNode, useState } from "react";
import { Backdrop } from "../components/Backdrop";
import { HideGifts } from "../components/HideGifts";
import { IconButton } from "../components/IconButton";
import { Icon } from "../components/icons";
import { LanguagePicker } from "../components/LanguagePicker";
import { PrefSwitch } from "../components/PrefSwitch";
import { type PrivacyState, usePrivacy } from "../components/usePrivacy";
import { openExternal } from "../discord/handshake";
import { useT } from "../i18n/context";
import { intlLocale } from "../i18n/index";
import { useStore } from "../store";

/** Where the legal texts live (review 0016): the public site, opened in the browser. */
export const LEGAL = {
  privacy: "https://highcard.club/privacy",
  terms: "https://highcard.club/terms",
} as const;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5 rounded-[16px] border-[1.5px] border-edge bg-panel-strong px-4 py-3.5 shadow-pill">
      <h2 className="m-0 text-[11px] font-bold uppercase tracking-[0.1em] text-ink/60">{title}</h2>
      {children}
    </section>
  );
}

const glass =
  "btn-glass h-11 self-start rounded-pill px-5 text-[14px] font-bold disabled:opacity-60";
const danger =
  "h-11 self-start rounded-pill border-[1.5px] border-danger-soft bg-ink/8 px-5 text-[14px] font-bold text-danger-soft disabled:opacity-60";

/** Asking for deletion, and the 30 days it waits (decision 88). */
function Deletion({ privacy }: { privacy: PrivacyState }) {
  const { t, locale } = useT();
  const [confirming, setConfirming] = useState(false);
  const due = privacy.status?.deletionDueAt ?? null;
  if (due !== null)
    return (
      <>
        <p role="status" className="m-0 text-[14px]">
          {t("data.delete.pending", {
            date: new Date(due).toLocaleDateString(intlLocale(locale), { dateStyle: "long" }),
          })}
        </p>
        <button
          type="button"
          className={glass}
          disabled={privacy.busy}
          onClick={() => void privacy.cancelDeletion()}
        >
          {t("data.delete.cancel")}
        </button>
      </>
    );
  if (!confirming)
    return (
      <>
        <p className="m-0 text-[13px] text-ink/75">{t("data.delete.about")}</p>
        <button type="button" className={danger} onClick={() => setConfirming(true)}>
          {t("data.delete.start")}
        </button>
      </>
    );
  return (
    <>
      <p className="m-0 text-[14px]">{t("data.delete.confirm")}</p>
      <div className="flex flex-wrap gap-2.5">
        <button
          type="button"
          className={danger}
          disabled={privacy.busy}
          onClick={() => void privacy.requestDeletion().then(() => setConfirming(false))}
        >
          {t("data.delete.yes")}
        </button>
        <button type="button" className={glass} onClick={() => setConfirming(false)}>
          {t("data.delete.keep")}
        </button>
      </div>
    </>
  );
}

/**
 * "My data" (review 0016, ADR 0031): the legal texts, hiding your name on the leaderboard, taking
 * part in usage measurement (ADR 0049), downloading your data, and deleting it after 30 days. See docs/ekranlar/verilerim.md.
 */
export function MyData({ onBack }: { onBack: () => void }) {
  const { t } = useT();
  const sdk = useStore((s) => s.sdk);
  const privacy = usePrivacy();
  const waiting = privacy.status === null || privacy.busy;
  return (
    <div className="relative isolate h-full overflow-y-auto font-ui text-ink">
      <Backdrop />
      <div className="relative mx-auto flex max-w-[600px] flex-col gap-4 px-5 pb-16 pt-6">
        <header className="flex items-center gap-3.5">
          <IconButton label={t("data.back")} size={52} onClick={onBack}>
            <Icon name="back" strokeWidth={2.4} />
          </IconButton>
          <h1 className="text-legible m-0 text-[26px] font-extrabold">{t("data.title")}</h1>
        </header>
        {privacy.failed && (
          <p
            role="alert"
            className="m-0 rounded-[12px] border border-danger-soft/70 bg-danger/30 px-3 py-2 text-[13px]"
          >
            {t("data.failed")}
          </p>
        )}
        <Section title={t("language.title")}>
          <LanguagePicker />
        </Section>
        <Section title={t("data.gifts")}>
          <HideGifts />
        </Section>
        <Section title={t("data.legal")}>
          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              className={glass}
              onClick={() => void openExternal(sdk, LEGAL.privacy)}
            >
              {t("legal.privacy")}
            </button>
            <button
              type="button"
              className={glass}
              onClick={() => void openExternal(sdk, LEGAL.terms)}
            >
              {t("legal.terms")}
            </button>
          </div>
        </Section>
        <Section title={t("data.leaderboard")}>
          <PrefSwitch
            label={t("data.hide")}
            about={t("data.hide.about")}
            checked={privacy.status?.hideOnLeaderboard ?? false}
            disabled={waiting}
            onChange={(hide) => void privacy.setPrefs({ hideOnLeaderboard: hide })}
          />
        </Section>
        <Section title={t("data.usage")}>
          <PrefSwitch
            label={t("data.measure")}
            about={t("data.measure.about")}
            checked={privacy.status?.measurement ?? true}
            disabled={waiting}
            onChange={(on) => void privacy.setPrefs({ measurement: on })}
          />
        </Section>
        <Section title={t("data.download")}>
          <p className="m-0 text-[13px] text-ink/75">{t("data.download.about")}</p>
          <button
            type="button"
            className={glass}
            disabled={privacy.busy}
            onClick={() => void privacy.download()}
          >
            {t("data.download.start")}
          </button>
        </Section>
        <Section title={t("data.delete")}>
          <Deletion privacy={privacy} />
        </Section>
      </div>
    </div>
  );
}
